"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useSyncExternalStore } from "react";
import { QueryKeys } from "@/lib/types";
import { collectInboxNotificationsFromCache } from "@/features/notifications/utils/notification-read-state";
import type { GetNotificationResponse } from "@/openapi/generated";

function inboxReadSnapshot(items: GetNotificationResponse[]): string {
  return items
    .map(
      (item) =>
        `${String(item.id).toLowerCase()}:${item.isRead ? "1" : "0"}`,
    )
    .sort()
    .join("|");
}

function sameInboxSnapshot(
  a: GetNotificationResponse[],
  b: GetNotificationResponse[],
): boolean {
  return inboxReadSnapshot(a) === inboxReadSnapshot(b);
}

/** Live GET `/notifications` rows so the bell can trust inbox `isRead`. */
export function useCachedInboxNotifications(userId: number) {
  const queryClient = useQueryClient();
  const snapshotRef = useRef<GetNotificationResponse[]>([]);

  const subscribe = useCallback((onStoreChange: () => void) => {
    let active = true;
    let queued = false;
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (!active || queued) return;
      if (event.query.queryKey[0] !== QueryKeys.Notifications) return;
      // Cache writes happen during hydration and page render. Notifying the
      // bell in that same turn updates NotificationButton while the other
      // component is still rendering.
      queued = true;
      queueMicrotask(() => {
        queued = false;
        if (active) onStoreChange();
      });
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [queryClient]);

  const getSnapshot = useCallback(() => {
    const next = collectInboxNotificationsFromCache(queryClient, userId);
    if (sameInboxSnapshot(snapshotRef.current, next)) {
      return snapshotRef.current;
    }
    snapshotRef.current = next;
    return next;
  }, [queryClient, userId]);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
